import { motion } from "framer-motion";
import { Flag, ClipboardCheck, ImageIcon, Quote } from "lucide-react";
import { business, services, gallery, reviews } from "../config/site.config";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay, ease: [0.2, 0.8, 0.2, 1] }}
  >
    {children}
  </motion.div>
);

const Placeholder = ({ children }) => (
  <span className="inline-flex items-center gap-2 border border-dashed border-current px-2.5 py-1 font-mono text-xs uppercase tracking-widest">{children}</span>
);

export const Marquee = () => {
  const items = [...business.serviceAreaShort, ...business.credentials];
  const row = [...items, ...items];
  return (
    <div className="overflow-hidden border-y border-charcoal/10 py-7" data-testid="marquee">
      <p className="sr-only">{`Serving ${business.serviceArea}. ${business.credentials.join(", ")}.`}</p>
      <div className="sab-marquee flex w-max items-center gap-10" aria-hidden="true">
        {[...row, ...row].map((t, i) => (
          <span key={i} className="flex items-center gap-10 font-display text-4xl font-bold italic tracking-tight text-charcoal sm:text-5xl">
            {t}
            <span className={`h-3 w-14 rounded-full ${i % 2 ? "bg-ember" : "bg-wildberry"}`} />
          </span>
        ))}
      </div>
    </div>
  );
};

// Order matches business.credentials: military owned, free estimates.
const ICONS = [Flag, ClipboardCheck];

export const Services = () => (
  <section id="services" className="on-dark bg-charcoal py-24 text-beige sm:py-32" data-testid="services-section">
    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
      <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-beige/70">Services</p>
          <h2 className="mt-5 font-heading text-4xl leading-[1.02] sm:text-5xl lg:text-6xl">Painting &amp; decorating, inside and out.</h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-beige/80">
            Serving {business.serviceArea}.
          </p>
          <ul className="mt-12 grid grid-cols-2 gap-px bg-beige/10" data-testid="credentials-list">
            {business.credentials.map((c, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <li key={c} className="flex items-center gap-3 bg-charcoal p-5">
                  <Icon className="h-5 w-5 shrink-0 text-ember-light" />
                  <span className="text-sm font-semibold">{c}</span>
                </li>
              );
            })}
          </ul>
        </Reveal>
        <ol className="border-t border-beige/15">
          {services.items.map((s, i) => (
            <li key={s.title} data-testid={`service-item-${i}`}>
              <Reveal delay={i * 0.06} className="group grid grid-cols-[3rem_1fr] gap-4 border-b border-beige/15 py-8 sm:grid-cols-[4rem_1fr_1fr] sm:gap-8">
                <span className="font-mono text-sm text-beige/60">0{i + 1}</span>
                <h3 className="font-heading text-3xl transition-transform duration-500 group-hover:translate-x-2 sm:text-4xl">{s.title}</h3>
                <p className="col-start-2 text-base leading-relaxed text-beige/80 sm:col-start-3">{s.text}</p>
              </Reveal>
            </li>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

export const Gallery = () => {
  if (!gallery.show) return null;
  const empty = gallery.items.length === 0;
  const items = empty ? [0, 1, 2] : gallery.items;
  return (
    <section id="projects" className="py-24 sm:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="eyebrow">Completed projects</p>
            <h2 className="mt-5 font-heading text-4xl leading-[1.02] text-charcoal sm:text-5xl lg:text-6xl">Real SAB work.</h2>
          </div>
          <p className="max-w-sm text-base text-charcoal-muted">Only SAB's own completed projects appear here — kept separate from the color inspiration scenes above.</p>
        </Reveal>
        {empty && <p className="mt-8 text-wildberry"><Placeholder>Placeholder · add project photos or hide this section</Placeholder></p>}
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {items.map((it, i) => (
            <Reveal key={i} delay={i * 0.08}>
              {empty ? (
                <div className={`flex aspect-[4/5] flex-col items-center justify-center gap-3 border border-dashed border-charcoal/30 bg-beige-2 text-charcoal-muted ${i === 1 ? "md:translate-y-10" : ""}`} data-testid={`gallery-placeholder-${i}`}>
                  <ImageIcon className="h-8 w-8" />
                  <span className="font-mono text-xs uppercase tracking-widest">Project photo {i + 1}</span>
                </div>
              ) : (
                <figure className={`group overflow-hidden ${i % 3 === 1 ? "md:translate-y-10" : ""}`} data-testid={`gallery-item-${i}`}>
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={it.src} alt={it.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <figcaption className="mt-3 flex justify-between text-sm"><span className="font-semibold">{it.title}</span><span className="text-charcoal-muted">{it.type}</span></figcaption>
                </figure>
              )}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Reviews = () => {
  if (!reviews.show) return null;
  const empty = reviews.items.length === 0;
  const items = empty ? [0, 1] : reviews.items;
  return (
    <section id="reviews" className="bg-beige-2 py-24 sm:py-32" data-testid="reviews-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <Reveal>
          <p className="eyebrow">Customer reviews</p>
          <h2 className="mt-5 font-heading text-4xl leading-[1.02] text-charcoal sm:text-5xl lg:text-6xl">In their words.</h2>
          {empty && <p className="mt-6 text-wildberry"><Placeholder>Placeholder · add real reviews or hide this section</Placeholder></p>}
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {items.map((r, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <figure className={`h-full border p-8 sm:p-10 ${empty ? "border-dashed border-charcoal/30" : "border-charcoal/10 bg-beige"}`} data-testid={`review-${i}`}>
                <Quote className="h-7 w-7 text-wildberry" />
                <blockquote className={`mt-6 font-display text-2xl leading-snug sm:text-3xl ${empty ? "text-charcoal-muted" : "text-charcoal"}`}>
                  {empty ? "[Customer review placeholder — add a real review, shared with the customer's permission.]" : `“${r.quote}”`}
                </blockquote>
                <figcaption className="mt-6 font-mono text-xs uppercase tracking-widest text-charcoal-muted">
                  {empty ? "[Customer name · Location]" : `${r.name}${r.location ? ` · ${r.location}` : ""}`}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
